import { useState, useRef, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useParams, useNavigate } from 'react-router-dom'
import { useIssue } from '../../context/IssueContext'
import { useTheme } from '../../context/ThemeContext'

const TOTAL_QUESTION_STEPS = 5

const slideVariants = {
  enter: (dir) => ({ x: dir * 48, opacity: 0 }),
  center: {
    x: 0,
    opacity: 1,
    transition: { duration: 0.28, ease: [0.25, 0.46, 0.45, 0.94] },
  },
  exit: (dir) => ({
    x: dir * -48,
    opacity: 0,
    transition: { duration: 0.18, ease: 'easeIn' },
  }),
}

export default function HowPage({ setOnNext }) {
  const { theme } = useTheme()
  const { shareId: routeShareId } = useParams()
  const { setShareId } = useIssue()
  const navigate = useNavigate()

  const [currentStep, setCurrentStep] = useState(1)
  const [direction, setDirection] = useState(1)
  const [answers, setAnswers] = useState(Array(TOTAL_QUESTION_STEPS).fill(''))
  const [submitting, setSubmitting] = useState(false)
  const [completed, setCompleted] = useState(false)
  const [submitError, setSubmitError] = useState('')
  const [hoveredNav, setHoveredNav] = useState(null)
  const [isCompactNav, setIsCompactNav] = useState(false)

  const inputRef = useRef(null)
  const topRef = useRef(null)
  const isDark = theme === 'dark'

  const submissionId = Number(localStorage.getItem('submissionId'))
  const API_BASE =
    process.env.REACT_APP_API_BASE_URL ||
    'https://codesign-project.onrender.com'

  useEffect(() => {
    if (routeShareId) setShareId(routeShareId)
  }, [routeShareId, setShareId])

  useEffect(() => {
    inputRef.current?.focus()
    topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [currentStep])

  useEffect(() => {
    const updateNavLayout = () => {
      setIsCompactNav(window.innerWidth < 480)
    }

    updateNavLayout()
    window.addEventListener('resize', updateNavLayout)
    return () => window.removeEventListener('resize', updateNavLayout)
  }, [])

  const submitHow = useCallback(async () => {
    if (submitting) return
    setSubmitting(true)
    setSubmitError('')

    try {
      const howRes = await fetch(`${API_BASE}/api/how`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          submissionId,
          shareId: routeShareId,
          answer1: answers[0],
          answer2: answers[1],
          answer3: answers[2],
          answer4: answers[3],
          answer5: answers[4],
        }),
      })
      if (!howRes.ok) throw new Error('Failed to submit how response')

      const sid = localStorage.getItem('submissionId')
      if (!sid) throw new Error('No submissionId found')

      const submitRes = await fetch(
        `${API_BASE}/api/submissions/${sid}/submit`,
        { method: 'POST' },
      )
      if (!submitRes.ok) throw new Error('Failed to submit feedback session')

      setCompleted(true)
    } catch (err) {
      console.error(err)
      setSubmitError('We could not save your How responses. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }, [API_BASE, answers, routeShareId, submissionId, submitting])

  const goNext = useCallback(async () => {
    if (completed) {
      navigate(routeShareId ? `/share/${routeShareId}/thankyou` : '/thankyou')
      return false
    }

    if (currentStep < TOTAL_QUESTION_STEPS) {
      if (!answers[currentStep - 1].trim()) return
      setDirection(1)
      setCurrentStep((s) => s + 1)
    } else {
      await submitHow()
    }

    return false
  }, [answers, completed, currentStep, navigate, routeShareId, submitHow])

  const goPrev = () => {
    if (currentStep === 1) return
    setDirection(-1)
    setCurrentStep((s) => s - 1)
  }

  const finishEarly = async () => {
    await submitHow()
  }

  useEffect(() => {
    if (setOnNext) setOnNext(() => goNext)
    return () => {
      if (setOnNext) setOnNext(null)
    }
  }, [setOnNext, goNext])

  // ── design tokens ────────────────────────────────────────────────
  const textColor = isDark ? '#f0f0f0' : '#1a1a1a'
  const subText = isDark ? '#888' : '#888'
  const inputBg = isDark ? '#1a1a1a' : '#ffffff'
  const inputBorder = isDark ? 'rgba(255,255,255,0.18)' : '#ced4da'
  const hintBg = isDark ? '#1f1f1f' : '#f8f9fa'
  const hintBorder = isDark ? 'rgba(255,255,255,0.08)' : '#e9ecef'
  const successBg = '#ffffff'
  const successLine = '#ffe071'
  const isLastStep = currentStep === TOTAL_QUESTION_STEPS
  const questionIdx = currentStep - 1

  if (completed) {
    return (
      <div
        ref={topRef}
        className="max-w-[640px] mx-auto px-4 font-poppins"
        style={{ color: textColor, paddingBottom: 32 }}
      >
        <motion.section
          initial={{ y: 18, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.28, ease: [0.25, 0.46, 0.45, 0.94] }}
          style={{
            border: `1px solid ${successLine}`,
            background: successBg,
            borderRadius: 12,
            padding: '28px 22px',
            boxShadow: isDark
              ? '0 10px 28px rgba(0,0,0,0.22)'
              : '0 10px 28px rgba(0,0,0,0.06)',
          }}
        >
          <div
            aria-hidden="true"
            style={{
              width: 44,
              height: 44,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#ffe071',
              color: '#1a1a1a',
              fontSize: 24,
              fontWeight: 800,
              marginBottom: 18,
            }}
          >
            ✓
          </div>

          <h1
            style={{
              margin: '0 0 10px',
              fontSize: 28,
              fontWeight: 800,
              color: '#1a1a1a',
            }}
          >
            Why/How ladder complete
          </h1>

          <h2
            style={{
              margin: '0 0 10px',
              fontSize: 20,
              fontWeight: 800,
              color: '#1a1a1a',
            }}
          >
            Thank you for sharing your lived wisdom!
          </h2>

          <p
            style={{
              margin: '0 0 20px',
              fontSize: 15,
              lineHeight: 1.7,
              color: subText,
            }}
          >
            Your responses have been saved. You can now finish the session or
            leave an email on the next screen if you would like updates or
            voucher information.
          </p>

          <button
            type="button"
            onClick={goNext}
            onMouseEnter={() => setHoveredNav('continue')}
            onMouseLeave={() => setHoveredNav(null)}
            style={{
              padding: '11px 24px',
              borderRadius: 10,
              border: 'none',
              background: hoveredNav === 'continue' ? '#ffd43b' : '#ffe071',
              color: '#1a1a1a',
              fontWeight: 700,
              fontSize: 14,
              fontFamily: 'Poppins, sans-serif',
              cursor: 'pointer',
              transform:
                hoveredNav === 'continue' ? 'translateY(-1px)' : 'none',
              boxShadow:
                hoveredNav === 'continue'
                  ? '0 4px 12px rgba(0,0,0,0.12)'
                  : 'none',
              transition: 'all 0.15s',
            }}
          >
            Continue
          </button>
        </motion.section>
      </div>
    )
  }

  return (
    <div
      ref={topRef}
      className="max-w-[640px] mx-auto px-4 font-poppins"
      style={{ color: textColor, paddingBottom: 32 }}
    >
      {/* ── Progress bar ── */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          marginBottom: 28,
        }}
      >
        <span
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: subText,
            whiteSpace: 'nowrap',
          }}
        >
          Question {currentStep} of {TOTAL_QUESTION_STEPS}
        </span>
        <div style={{ flex: 1, display: 'flex', gap: 4 }}>
          {Array.from({ length: TOTAL_QUESTION_STEPS }).map((_, i) => (
            <div
              key={i}
              style={{
                flex: 1,
                height: 4,
                borderRadius: 2,
                background:
                  i < currentStep
                    ? '#ffe071'
                    : isDark
                      ? 'rgba(255,255,255,0.1)'
                      : '#e0e0e0',
                transition: 'background 0.25s',
              }}
            />
          ))}
        </div>
      </div>

      {/* ── Animated step content ── */}
      <AnimatePresence mode="wait" custom={direction}>
        <motion.div
          key={currentStep}
          custom={direction}
          variants={slideVariants}
          initial="enter"
          animate="center"
          exit="exit"
        >
          {/* Question label */}
          <p
            style={{
              fontWeight: 600,
              fontSize: 20,
              color: textColor,
              marginBottom: 60,
            }}
          >
            {currentStep === 1
              ? 'How could this be improved?'
              : 'How could that be improved?'}
          </p>

          {/* Info hint */}
          <div
            style={{
              display: 'flex',
              gap: 10,
              alignItems: 'flex-start',
              padding: '12px 14px',
              borderRadius: 10,
              background: hintBg,
              marginBottom: 16,
              fontSize: 13,
              color: subText,
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: 20,
                height: 20,
                minWidth: 20,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: isDark ? 'rgba(255,255,255,0.08)' : '#e9ecef',
                fontSize: 11,
                fontWeight: 700,
                color: textColor,
                marginTop: 1,
              }}
            >
              i
            </span>
            Write in your own words. No names or identifiers.
          </div>

          {/* Textarea */}
          <textarea
            ref={inputRef}
            placeholder="Type your answer here…"
            value={answers[questionIdx]}
            onChange={(e) => {
              const next = [...answers]
              next[questionIdx] = e.target.value
              setAnswers(next)
            }}
            style={{
              width: '100%',
              minHeight: 130,
              padding: '12px 14px',
              borderRadius: 10,
              border: `1.5px solid ${inputBorder}`,
              background: inputBg,
              color: textColor,
              fontSize: 15,
              fontFamily: 'Poppins, sans-serif',
              lineHeight: 1.55,
              resize: 'vertical',
              outline: 'none',
              boxSizing: 'border-box',
              transition: 'border-color 0.15s',
            }}
            onFocus={(e) => {
              e.target.style.borderColor = '#ffe071'
            }}
            onBlur={(e) => {
              e.target.style.borderColor = inputBorder
            }}
          />

          {submitError && (
            <div
              role="alert"
              style={{
                marginTop: 12,
                padding: '10px 12px',
                borderRadius: 10,
                border: `1px solid ${hintBorder}`,
                background: isDark ? 'rgba(248,113,113,0.10)' : '#fff5f5',
                color: isDark ? '#fecaca' : '#b42318',
                fontSize: 13,
                lineHeight: 1.5,
              }}
            >
              {submitError}
            </div>
          )}

          {/* Navigation row */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-end',
              marginTop: 14,
              gap: 10,
              flexWrap: 'nowrap',
            }}
          >
            {/* Back */}
            <button
              onClick={goPrev}
              disabled={currentStep === 1}
              onMouseEnter={() => currentStep !== 1 && setHoveredNav('back')}
              onMouseLeave={() => setHoveredNav(null)}
              style={{
                padding: isCompactNav ? '9px 12px' : '10px 18px',
                borderRadius: 10,
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : '#ddd'}`,
                background:
                  hoveredNav === 'back'
                    ? isDark
                      ? 'rgba(255,255,255,0.06)'
                      : 'rgba(0,0,0,0.04)'
                    : 'transparent',
                color:
                  currentStep === 1
                    ? isDark
                      ? 'rgba(255,255,255,0.2)'
                      : '#ccc'
                    : textColor,
                fontWeight: 600,
                fontSize: isCompactNav ? 12 : 14,
                fontFamily: 'Poppins, sans-serif',
                cursor: currentStep === 1 ? 'not-allowed' : 'pointer',
                whiteSpace: 'nowrap',
                flexShrink: 0,
                transition: 'all 0.15s',
              }}
            >
              {isCompactNav ? 'Prev' : '← Previous'}
            </button>

            {/* Right side: I don't know + Next/Finish */}
            <div
              style={{
                display: 'flex',
                gap: isCompactNav ? 6 : 8,
                alignItems: 'flex-end',
                flexWrap: 'nowrap',
                justifyContent: 'flex-end',
                minWidth: 0,
                flex: 1,
              }}
            >
              {currentStep >= 2 && (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 4,
                    alignItems: 'flex-end',
                    minWidth: 0,
                    flexShrink: 1,
                  }}
                >
                  <span
                    style={{
                      display: isCompactNav ? 'none' : 'block',
                      fontSize: 11,
                      color: subText,
                      maxWidth: 200,
                      textAlign: 'right',
                      lineHeight: 1.4,
                    }}
                  >
                    Unsure how to continue? This would end follow-ups.
                  </span>
                  <button
                    onClick={finishEarly}
                    disabled={submitting}
                    onMouseEnter={() =>
                      !submitting && setHoveredNav('idontknow')
                    }
                    onMouseLeave={() => setHoveredNav(null)}
                    style={{
                      padding: isCompactNav ? '9px 12px' : '10px 18px',
                      borderRadius: 10,
                      border: `1px solid ${isDark ? 'rgba(255,255,255,0.18)' : '#bbb'}`,
                      background:
                        hoveredNav === 'idontknow'
                          ? isDark
                            ? 'rgba(255,255,255,0.08)'
                            : 'rgba(0,0,0,0.05)'
                          : 'transparent',
                      color: textColor,
                      fontWeight: 600,
                      fontSize: isCompactNav ? 12 : 14,
                      fontFamily: 'Poppins, sans-serif',
                      cursor: submitting ? 'not-allowed' : 'pointer',
                      opacity: submitting ? 0.6 : 1,
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                      transition: 'all 0.15s',
                    }}
                  >
                    {isCompactNav ? 'Unsure' : "I don't know"}
                  </button>
                </div>
              )}

              <button
                onClick={goNext}
                disabled={submitting || !answers[questionIdx].trim()}
                onMouseEnter={() =>
                  answers[questionIdx].trim() &&
                  !submitting &&
                  setHoveredNav('next')
                }
                onMouseLeave={() => setHoveredNav(null)}
                style={{
                  padding: isCompactNav ? '9px 12px' : '10px 22px',
                  borderRadius: 10,
                  border: 'none',
                  background:
                    answers[questionIdx].trim() && !submitting
                      ? hoveredNav === 'next'
                        ? '#ffd43b'
                        : '#ffe071'
                      : isDark
                        ? 'rgba(255,255,255,0.08)'
                        : '#e0e0e0',
                  color:
                    answers[questionIdx].trim() && !submitting
                      ? '#1a1a1a'
                      : subText,
                  fontWeight: 700,
                  fontSize: isCompactNav ? 12 : 14,
                  fontFamily: 'Poppins, sans-serif',
                  cursor:
                    answers[questionIdx].trim() && !submitting
                      ? 'pointer'
                      : 'not-allowed',
                  transform:
                    answers[questionIdx].trim() &&
                    !submitting &&
                    hoveredNav === 'next'
                      ? 'translateY(-1px)'
                      : 'none',
                  boxShadow:
                    answers[questionIdx].trim() &&
                    !submitting &&
                    hoveredNav === 'next'
                      ? '0 4px 12px rgba(0,0,0,0.12)'
                      : 'none',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  transition: 'all 0.15s',
                }}
              >
                {submitting
                  ? 'Submitting…'
                  : isCompactNav
                    ? isLastStep
                      ? 'Finish'
                      : 'Next'
                    : isLastStep
                      ? 'Finish ✓'
                      : 'Next question →'}
              </button>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  )
}
